// workspaceSettings.js — THE one accessor for `root.workspace.settings`
// (tickets/spec-workspace-record-shape.md §5, 2026-08-08).
//
// WHY THIS EXISTS. `root.workspace` is populated from two incompatible shapes
// (see activeWorkspaceRecord.js for the full history): a FULL server record,
// which carries the `settings` bag — `navbar`, `workspaceApps`,
// `home_default_panels`, `featureFlags`, `appAccessControl`, `voice`, `ai`,
// `companyInfo`, `workspaceModule` — and a LIST/claims entry, which never
// does. §4 made the downgrade impossible; it did NOT make the *pre-arrival*
// window visible. Thirty-five direct `workspace.settings.*` reads each did
//
//     const list = root?.workspace?.settings?.workspaceApps    // → undefined
//
// and then fell through to a default. `undefined` there has TWO meanings that
// the optional-chain collapses into one:
//
//   (a) the workspace is loaded and legitimately has no override — the
//       default IS the answer; and
//   (b) the workspace record has not arrived yet — the default is a LIE, and
//       rendering it paints a configured workspace as an unconfigured one.
//
// THE RULE (spec §5): a not-yet-loaded workspace must never render as a
// legitimately-configured one. So every read goes through here and gets the
// disambiguating bit back with the data:
//
//     const { settings, ready, pending } = workspaceSettings(root)
//
//   ready === true   → `settings` is authoritative (possibly `{}`, which
//                      means "loaded, no overrides" — a real answer).
//   ready === false  → `settings` is null. NOT "no overrides". Unknown.
//
// READINESS IS A TRI-STATE, and collapsing it to a boolean is a trap worth
// naming, because it is how a "correct" render gate turns into a permanently
// blank surface:
//
//   ready              the record is here. Render it.
//   pending            an id is known and completion is genuinely in flight
//                      (§4c scheduled the fetch). Render the indeterminate
//                      state — NOT the default.
//   !ready && !pending either no workspace is selected at all, or completion
//                      definitively FAILED (`_completionUnreachable`, stamped
//                      by activeWorkspaceRecord.js when the follow-up fetch
//                      comes back empty). Nothing more is coming, so the
//                      default IS the best available answer and rendering it
//                      is honest, not a substitution.
//
// Without that third state a render gate written as `if (!ready) return []`
// blanks the navbar FOREVER for any user whose getWorkspace 403s — trading a
// transient wrong-navbar bug for a permanent no-navbar one.
//
// HOW TO USE THE BIT. Two call-site classes, and they behave differently on
// purpose:
//
//   DECISION sites — read a setting to take a branch where "absent" and
//     "unknown" lead to the same safe action (is this flag on? is enforcement
//     enabled? is there a voice agent configured?). These may ignore `ready`,
//     but must say so in a comment: the default they fall to has to be the
//     right answer for BOTH meanings, and that is a claim worth writing down.
//
//   RENDER sites — paint configuration into the UI (the navbar, an
//     enforcement toggle, an "Added to workspace" label, a configured agent
//     id). These MUST branch on `ready` and render a loading/indeterminate
//     state instead of the default. This is the same principle the
//     app-dependency spec states for missing dependencies: a missing value
//     must be visually distinct from an empty one, never silently
//     substituted.
//
//   WRITE-BASE sites — read the bag only to build the next one
//     (`{ ...ws.settings, voice: next }`). These must NOT write while
//     `ready` is false: a merge over a null base both materializes a fake
//     `settings` bag on a shape that has none (making the record LOOK full)
//     and can drop sibling keys the writer never saw. `workspaceSettingsForWrite`
//     below is the guarded form.
//
// This accessor is deliberately NOT a fetch. It reports what is held right
// now. Convergence — "an id is known, so the full record WILL arrive" — is
// activeWorkspaceRecord.js's job (§4c), and the late-arrival re-render is
// §6's (see notifyActiveWorkspaceFull / the applyWorkspaceConfig converge in
// workspace/functions/loadPrivateData.js).

import { isFullWorkspaceRecord } from './activeWorkspaceRecord.js'

// Accept whatever the call site has in hand: a DOMQL state (`s`, which
// carries `.root`), a root directly, or nothing at all when invoked as
// `el.call('workspaceSettings')` (then `this.state.root`). One accessor with
// 35 call sites cannot afford to make each one remember which it holds.
//
// The `s.root` probe must come first and must be shape-checked: a ROOT's own
// `.root` is itself (DOMQL roots are self-rooted), so an unguarded
// `input.root || input` would loop a root back through correctly by accident
// but send a detached child state somewhere unexpected. Testing for the
// workspace-bearing surface keeps it explicit.
//
// FA204 / frank trap — this used to be one module-scope helper shared by all
// four exports below via a bare reference, which breaks post-frank-
// serialization (see appAccess.js/screenshot.js for the identical shape of
// fix). Each export now carries its own self-contained copy.

// el.call('workspaceSettings', s) → { settings, ready, workspace, id }
// Also callable as a plain function: workspaceSettings(root).
export function workspaceSettings(input) {
  const rootOf = (i, self) => {
    if (i && typeof i === 'object') {
      if (i.root && typeof i.root === 'object') return i.root
      if ('workspace' in i || typeof i.update === 'function') return i
    }
    if (self && self.state) return self.state.root || self.state
    return null
  }
  const root = rootOf(input, this)
  const ws = (root && root.workspace) || null
  // Readiness is the PROVENANCE stamp, never "settings is non-empty" — a
  // workspace may legitimately carry no overrides at all, and inferring
  // not-loaded from an empty bag is exactly the conflation this file exists
  // to end (spec §4d).
  const ready = isFullWorkspaceRecord(ws)
  const id = ws ? String(ws.id || ws._id || '') || null : null
  return {
    // `{}` when ready-with-no-overrides; `null` when not ready. Callers that
    // optional-chain (`settings?.navbar`) get `undefined` either way — which
    // is why the ones that RENDER must read `ready`, not just `settings`.
    settings: ready ? ws.settings || {} : null,
    ready,
    // See the tri-state note at the top of this file. `_completionUnreachable`
    // is the terminal stamp activeWorkspaceRecord.js writes when the §4c
    // follow-up fetch cannot produce a full record.
    pending: !ready && !!id && !ws._completionUnreachable,
    workspace: ws,
    id
  }
}

// Sugar for the very common `if (!ready) return <indeterminate>` guard at a
// render site, where the settings bag itself isn't needed.
export function workspaceSettingsReady(input) {
  const rootOf = (i, self) => {
    if (i && typeof i === 'object') {
      if (i.root && typeof i.root === 'object') return i.root
      if ('workspace' in i || typeof i.update === 'function') return i
    }
    if (self && self.state) return self.state.root || self.state
    return null
  }
  return isFullWorkspaceRecord(rootOf(input, this)?.workspace)
}

// The render gate, named for what it means at the call site: "hold off — the
// answer is coming". True ONLY in the genuinely-in-flight window, so a render
// site guarding on it degrades to its default when completion is impossible
// rather than staying indeterminate forever.
//
// Deliberately duplicates workspaceSettings' body rather than referencing it
// (bare sibling-export reads are the same frank trap this file exists to
// avoid, and `this` must forward transparently whether this is invoked
// directly or via el.call — see workspaceSettings above for the annotated
// original).
export function workspaceSettingsPending(input) {
  const rootOf = (i, self) => {
    if (i && typeof i === 'object') {
      if (i.root && typeof i.root === 'object') return i.root
      if ('workspace' in i || typeof i.update === 'function') return i
    }
    if (self && self.state) return self.state.root || self.state
    return null
  }
  const root = rootOf(input, this)
  const ws = (root && root.workspace) || null
  const ready = isFullWorkspaceRecord(ws)
  const id = ws ? String(ws.id || ws._id || '') || null : null
  return !ready && !!id && !ws?._completionUnreachable
}

// WRITE-BASE guard. Returns the bag to merge over, or `null` when the record
// is not complete enough to safely merge — in which case the caller must
// ABORT the write, not merge over `{}`.
//
// Why aborting is right: `sdk.updateWorkspaceSettings` is merge-safe per-key
// server-side, so a write of ONE key never drops siblings on the server. But
// every caller also mirrors the merged bag back onto `root.workspace` for
// optimistic paint, and a mirror built over a missing base publishes a
// `settings` bag that describes nothing — a record that then LOOKS full to
// any reader that (wrongly) infers readiness from settings presence. Worse,
// the workspaceApps CAS baseline is computed from this bag: a baseline of
// `[]` read from an unloaded record is precisely the stale writer that drops
// apps it never saw (tickets/server.md).
export function workspaceSettingsForWrite(input) {
  const rootOf = (i, self) => {
    if (i && typeof i === 'object') {
      if (i.root && typeof i.root === 'object') return i.root
      if ('workspace' in i || typeof i.update === 'function') return i
    }
    if (self && self.state) return self.state.root || self.state
    return null
  }
  const root = rootOf(input, this)
  const ws = (root && root.workspace) || null
  const ready = isFullWorkspaceRecord(ws)
  return ready ? ws.settings || {} : null
}
